import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Facebook, Instagram } from 'lucide-react';
import HeroSection from '../components/HeroSection';
import GiftStrip from '../components/GiftStrip';
import LunchResetStrip from '../components/LunchResetStrip';
import ServicesSection from '../components/ServicesSection';
import LatestBlogSection from '../components/LatestBlogSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';

const Home = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="transition-transform duration-300 group-hover:scale-105">
                <p className={`text-2xl font-serif tracking-wide transition-colors duration-300 ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}>
                  AYUMI SUZUKI
                </p>
                <p className={`text-xs tracking-widest transition-colors duration-300 ${
                  scrolled ? 'text-gray-600' : 'text-white/90'
                }`}>
                  certified massage therapist
                </p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/about"
                className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
              >
                About
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              {['Services', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                    scrolled ? 'text-gray-800' : 'text-white'
                  }`}
                >
                  {item}
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
                </button>
              ))}
              <Link
                to="/blog"
                className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
              >
                Blog
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/faq"
                className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
              >
                FAQ
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/gift-certificates"
                className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
                data-testid="nav-gift-link-desktop"
              >
                Gift
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <Link
                to="/book"
                className={`text-sm tracking-wide transition-all duration-300 hover:scale-105 relative group ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
              >
                Book
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal-500 transition-all duration-300 group-hover:w-full"></span>
              </Link>
              <div className="flex items-center space-x-4">
                <a
                  href="https://www.facebook.com/profile.php?id=61580747109661"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit our Facebook page"
                  className={`transition-all duration-300 hover:scale-110 ${
                    scrolled ? 'text-gray-800 hover:text-teal-600' : 'text-white hover:text-teal-300'
                  }`}
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.instagram.com/ayumimassage/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit our Instagram page"
                  className={`transition-all duration-300 hover:scale-110 ${
                    scrolled ? 'text-gray-800 hover:text-teal-600' : 'text-white hover:text-teal-300'
                  }`}
                >
                  <Instagram size={20} />
                </a>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`md:hidden transition-colors duration-300 ${
                scrolled ? 'text-gray-800' : 'text-white'
              }`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-4 pt-2 pb-4 space-y-2">
              <Link
                to="/about"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                About
              </Link>
              {['Services', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    scrollToSection(item.toLowerCase());
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                >
                  {item}
                </button>
              ))}
              <Link
                to="/blog"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                Blog
              </Link>
              <Link
                to="/faq"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                FAQ
              </Link>
              <Link
                to="/gift-certificates"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
                data-testid="nav-gift-link-mobile"
              >
                Gift Certificates
              </Link>
              <Link
                to="/book"
                onClick={() => setIsMenuOpen(false)}
                className="block w-full text-left px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                Book
              </Link>
              <a
                href="https://www.facebook.com/profile.php?id=61580747109661"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                <Facebook size={20} />
                <span>Facebook</span>
              </a>
              <a
                href="https://www.instagram.com/ayumimassage/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 px-3 py-2 text-gray-800 hover:bg-teal-50 rounded-lg transition-colors duration-200"
              >
                <Instagram size={20} />
                <span>Instagram</span>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Sections */}
      <HeroSection />
      <GiftStrip />
      <LunchResetStrip />
      <ServicesSection />
      <LatestBlogSection />
      <ContactSection />
      <Footer scrollToSection={scrollToSection} />
    </div>
  );
};

export default Home;